import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Stack,
  MenuItem,
  Pagination,
} from "@mui/material";

import TableChartIcon from "@mui/icons-material/TableChart";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SortIcon from "@mui/icons-material/Sort";
import CategoryIcon from "@mui/icons-material/Category";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";

import { alpha } from "@mui/material/styles";
import { useLocation, useParams, Outlet } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";

import {
  fetchWorkOrders,
  fetchStatuses,
  fetchAssignableUsers,
  fetchWorkOrderTypes,
} from "./Utils/workOrders.api";

import {
  getAssignedUserName,
  getTypeName,
  getStatusInfo,
} from "./Utils/workOrder.utils";

import WorkOrderCard from "./WorkOrderCard";
import WorkOrderTable from "./WorkOrderTable";
import StatusFilterCards from "./StatusFilterCards";

/* ------------------------------------------------------------------ */
/* Neighborhood labels & helpers (module scope; used in both components) */
/* ------------------------------------------------------------------ */
const GROUP_NAME_BY_CODE = {
  "EH CH HILL": "Estate Homes",
  "EH EDGMT": "Estate Homes",
  SV: "Sommerset Villas",
  SC: "Southmont Cove",
  SW: "Sutton Walk",
  "VILLA HMS": "Villa Homes",
  WF: "Waterford",
  WW: "Wedgewood",
};

const norm = (v) => (v ?? "").toString().trim();
const canon = (v) => norm(v).replace(/\s+/g, " ").toUpperCase();
const isRenter = (v) => /\bRENTER\b/i.test(v);

// New normalization: strip trailing numbers or dashes for known prefixes
const normalizeMembershipCode = (v) => {
  const c = canon(v);

  // Handle SV / SC / SW / WF / WW variants like "SV 2", "SC-3", "WF 10"
  if (/^(SV|SC|SW|WF|WW)\b/.test(c)) {
    return c.match(/^(SV|SC|SW|WF|WW)/)[1];
  }

  return c;
};

const groupNameFor = (raw) => {
  const c = normalizeMembershipCode(raw);
  return GROUP_NAME_BY_CODE[c] || norm(raw);
};

const groupIdFor = (raw) => canon(groupNameFor(raw)); // stable id like "ESTATE HOMES"




/* ------------------------------------------------------------------ */
/* Apple-ish Filter Bar (self-contained, only UI)                      */
/* ------------------------------------------------------------------ */
const FilterBar = ({
  viewMode,
  setViewMode,
  searchTerm,
  setSearchTerm,
  filter,
  setFilter,
  sortOrder,
  setSortOrder,
  selectedType,
  setSelectedType,
  selectedNeighborhood,
  setSelectedNeighborhood,
  workOrderTypes,
  neighborhoodOptions,
  onReset,
  hideSpecialProperty,
  setHideSpecialProperty,
}) => {
  // Toggle 'below' or 'above' to reposition the search row.
  const SEARCH_POSITION = "below"; // 'below' | 'above'

  const hasActive =
    !!searchTerm ||
    !!filter.startDate ||
    !!filter.endDate ||
    !!selectedType ||
    !!selectedNeighborhood;

  const typeLabel =
    selectedType &&
    (getTypeName(workOrderTypes, selectedType) ||
      workOrderTypes.find((t) => String(t.id) === String(selectedType))?.name ||
      selectedType);

  const dateLabel =
    filter.startDate || filter.endDate
      ? `${filter.startDate || "…"} – ${filter.endDate || "…"}`
      : "";

  // Reusable search row
  const SearchRow = (
    <Box sx={{ mt: 1.25 }}>
      <TextField
        fullWidth
        size="small"
        value={searchTerm}
        placeholder="Search (order #, address, type, priority, opened by, assigned user…)"
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": { borderRadius: 999 },
        }}
        onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
      />
    </Box>
  );

  return (
    <Box sx={{ pb: 2 }}>
      <Paper
        elevation={0}
        sx={(theme) => ({
          px: 2,
          py: 2,
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
          backdropFilter: "saturate(180%) blur(12px)",
          background:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.6)
              : "rgba(255,255,255,0.7)",
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 6px 20px ${alpha("#000", 0.5)}`
              : `0 8px 30px ${alpha("#000", 0.06)}`,
        })}
      >
        {/* Title + view switch */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            mb: 1,
          }}
        >
          
          <Typography variant="h5" sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
            Work Orders
          </Typography>

          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            sx={{
              borderRadius: 999,
              overflow: "hidden",
              "& .MuiToggleButton-root": {
                px: 1.75,
                border: "none",
                textTransform: "none",
                borderRadius: 0,
              },
            }}
          >
            <ToggleButton value="cards">
              <ViewModuleIcon sx={{ mr: 1 }} /> Cards
            </ToggleButton>
            <ToggleButton value="table">
              <TableChartIcon sx={{ mr: 1 }} /> Table
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Search row ABOVE filters (optional) */}
        {SEARCH_POSITION === "above" && SearchRow}

        {/* Filters row */}
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md="auto">
            <TextField
              type="date"
              label="Start"
              size="small"
              value={filter.startDate}
              onChange={(e) => setFilter((f) => ({ ...f, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonthIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 170, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
<Grid item xs="auto">
  {hasActive && (
    <Button
      size="small"
      variant="outlined"
      startIcon={<ClearRoundedIcon />}
      onClick={onReset}
      sx={{ borderRadius: 999, px: 2, height: 40, mr: 1 }}
    >
      Reset
    </Button>
  )}
  
</Grid>

          <Grid item xs={12} sm={6} md="auto">
            <TextField
              type="date"
              label="End"
              size="small"
              value={filter.endDate}
              onChange={(e) => setFilter((f) => ({ ...f, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonthIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 160, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md="auto">
            <TextField
              select
              label="Sort by"
              size="small"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              sx={{ minWidth: 220, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SortIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            >
             <MenuItem value="status">Status</MenuItem>
 
              <MenuItem value="newest">Newest to Oldest</MenuItem>
              <MenuItem value="oldest">Oldest to Newest</MenuItem>
              <MenuItem value="order_id_asc">Work Order # Ascending</MenuItem>
              <MenuItem value="order_id_desc">Work Order # Descending</MenuItem>
              <MenuItem value="type_asc">Type (A→Z)</MenuItem>
              <MenuItem value="type_desc">Type (Z→A)</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md="auto">
            <TextField
              select
              label="Request Type"
              size="small"
              value={selectedType}
              onChange={(e) => setSelectedType(String(e.target.value))}
              sx={{ minWidth: 220, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              SelectProps={{ displayEmpty: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CategoryIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">
                <em>All Types</em>
              </MenuItem>
              {workOrderTypes
                .slice()
                .sort((a, b) =>
                  (a.name || a.type || "").localeCompare(b.name || b.type || "")
                )
                .map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name || t.type || t.id}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md="auto">
            <TextField
              select
              label="Neighborhood"
              size="small"
              value={selectedNeighborhood}
              onChange={(e) => setSelectedNeighborhood(e.target.value)}
              sx={{ minWidth: 220, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              SelectProps={{ displayEmpty: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">
                <em>All Neighborhoods</em>
              </MenuItem>
              {neighborhoodOptions.map(({ code, label }) => (
                <MenuItem key={code} value={code}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

<Grid item xs={12} sm={6} md="auto">
  <ToggleButton
    value="hide008000"
    selected={hideSpecialProperty}
    onChange={() => setHideSpecialProperty(v => !v)}
    sx={{ borderRadius: 999, px: 2, height: 40 }}
    color={hideSpecialProperty ? "error" : "primary"} 
  >
    {hideSpecialProperty ? "Show 008000" : "Hide 008000"}
  </ToggleButton>
</Grid>

          <Grid item xs="auto">
            {hasActive && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ClearRoundedIcon />}
                onClick={onReset}
                sx={{ borderRadius: 999, px: 2, height: 40 }}
              >
                Reset
              </Button>
            )}
          </Grid>
        </Grid>

        {/* Search row BELOW filters (default) */}
        {SEARCH_POSITION === "below" && SearchRow}

        {/* Active filter chips */}
        {hasActive && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
            {searchTerm && (
              <Chip
                label={`Search: ${searchTerm}`}
                onDelete={() => setSearchTerm("")}
                size="small"
                variant="outlined"
              />
            )}
            {dateLabel && (
              <Chip
                label={`Date: ${dateLabel}`}
                onDelete={() => setFilter({ startDate: "", endDate: "" })}
                size="small"
                variant="outlined"
              />
            )}
            {selectedType && (
              <Chip
                label={`Type: ${typeLabel}`}
                onDelete={() => setSelectedType("")}
                size="small"
                variant="outlined"
              />
            )}
       {selectedNeighborhood && (
   <Chip
     label={`Neighborhood: ${
       neighborhoodOptions.find(n => n.code === selectedNeighborhood)?.label
       || selectedNeighborhood
     }`}
     onDelete={() => setSelectedNeighborhood("")}
     size="small"
     variant="outlined"
   />
 )}
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Main Page                                                           */
/* ------------------------------------------------------------------ */
const WorkOrdersPage = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [filteredWorkOrders, setFilteredWorkOrders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [workOrderTypes, setWorkOrderTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("cards");
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [filter, setFilter] = useState({ startDate: "", endDate: "" });
  const [selectedType, setSelectedType] = useState("");
  const [selectedNeighborhood, setSelectedNeighborhood] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 18;
  const location = useLocation();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("status");
  const [hideSpecialProperty, setHideSpecialProperty] = useState(false);


  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.state?.reload) loadInitialData();
  }, [location.state]);

  // Re-apply filters when inputs change
  useEffect(() => {
    applyFilters();
  }, [
    searchTerm,
    filterStatuses,
    filter,
    workOrders,
    selectedType,
    selectedNeighborhood,
    workOrderTypes,
    hideSpecialProperty,
  ]);



  // Reset to page 1 on filter/sort changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterStatuses, filter, selectedType, selectedNeighborhood, sortOrder]);

const neighborhoodOptions = useMemo(() => {
  const map = new Map(); // groupId -> { code, label }
  for (const o of workOrders || []) {
    const raw = norm(o?.ClubMembershipTypeCode);
    if (!raw || isRenter(raw)) continue; // skip renter rows

    const id = groupIdFor(raw);     // e.g. "ESTATE HOMES"
    const label = groupNameFor(raw); // e.g. "Estate Homes"
    map.set(id, { code: id, label });
  }
  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
}, [workOrders]);


  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [orders, statusList, users, types] = await Promise.all([
        fetchWorkOrders(),
        fetchStatuses(),
        fetchAssignableUsers(),
        fetchWorkOrderTypes(),
      ]);

      const data = (orders.data || []).filter((o) => o && o.order_id);
      setWorkOrders(data);
      setStatuses(statusList.data || []);
      setAssignableUsers(users.data || []);

      // Normalize types to { id, name }
      setWorkOrderTypes(
        (types.data || []).map((t) => ({
          ...t,
          id: String(t?.id ?? t?.workorder_type_id ?? t?.type_id),
          name: String(
            t?.name ??
              t?.workorder_type_name ??
              t?.type_name ??
              t?.title ??
              t?.display_name ??
              t?.Description ??
              t?.TypeName ??
              ""
          ),
        }))
      );

      const counts = {};
      for (const o of data) counts[o.status] = (counts[o.status] || 0) + 1;
      setStatusCounts(counts);
    } catch (error) {
      console.error("Failed to load work orders:", error);
    } finally {
      setLoading(false);
    }
  };

const filteredStatusCounts = useMemo(() => {
  const counts = {};
  for (const o of filteredWorkOrders) {
    counts[o.status] = (counts[o.status] || 0) + 1;
  }
  return counts;
}, [filteredWorkOrders]);


  const handleResetFilters = () => {
    setSearchTerm("");
    setFilterStatuses([]);
    setFilter({ startDate: "", endDate: "" });
    setSelectedType("");
    setSelectedNeighborhood("");
  };

  const applyFilters = () => {
  let filtered = [...workOrders];

  const terms = searchTerm
    .toLowerCase()
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

  if (terms.length > 0) {
    filtered = filtered.filter((order) => {
      const assigneeName =
        getAssignedUserName(assignableUsers, order.assign_to)?.toLowerCase() || "";
      const openerName =
        order.opened_by_name?.toString().toLowerCase() ||
        getAssignedUserName(assignableUsers, order.opened_by_user_id)?.toLowerCase() ||
        "";
      const typeText =
        getTypeName(workOrderTypes, order.workorder_type_id)?.toLowerCase() ||
        order.type_name?.toString().toLowerCase() ||
        "";

      const combined = [
        order.order_id,
        order.property_id,
        order.fullname ?? "",
        order.description,
        order.created_at,
        order.priority ?? "",
        order.full_address ?? "",
        order.ClubMembershipTypeCode ?? "",
        assigneeName,
        openerName,
        order.opened_by_user_id ?? "",
        order.status,
        getTypeName(workOrderTypes, order.workorder_type_id) ?? "",
        typeText,
        order.workorder_type_id ?? "",
        order.subtype_name ?? "",
        order.location_name ?? "",
      ]
        .map((x) => x?.toString().toLowerCase() || "")
        .join(" ");

      return terms.some((term) => combined.includes(term));
    });
  }

  if (filterStatuses.length) {
    filtered = filtered.filter((order) => filterStatuses.includes(order.status));
  }

  if (filter.startDate || filter.endDate) {
    filtered = filtered.filter((order) => {
      const date = String(order.created_at).slice(0, 10); // YYYY-MM-DD
      return (
        (!filter.startDate || date >= filter.startDate) &&
        (!filter.endDate || date <= filter.endDate)
      );
    });
  }

  if (selectedType) {
    filtered = filtered.filter(
      (order) => String(order.workorder_type_id) === String(selectedType)
    );
  }

  if (selectedNeighborhood) {
    filtered = filtered.filter(
      (order) => groupIdFor(order?.ClubMembershipTypeCode) === selectedNeighborhood
    );
  }

  // ✅ Hide property 008000 toggle here
  if (hideSpecialProperty) {
    filtered = filtered.filter(
      (order) => String(order.property_id).trim() !== "008000"
    );
  }

  setFilteredWorkOrders(filtered);
};

  const handleStatusFilter = (status) => {
    setFilterStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  
// Define status order once in your component


const sortedWorkOrders = [...filteredWorkOrders].sort((a, b) => {
  if (sortOrder === "newest") {
    return new Date(b.created_at) - new Date(a.created_at);
  } else if (sortOrder === "oldest") {
    return new Date(a.created_at) - new Date(b.created_at);
  } else if (sortOrder === "order_id_asc") {
    return Number(a.order_id) - Number(b.order_id);
  } else if (sortOrder === "order_id_desc") {
    return Number(b.order_id) - Number(a.order_id);
  } else if (sortOrder === "type_asc" || sortOrder === "type_desc") {
    const aType = (getTypeName(workOrderTypes, a.workorder_type_id) || "").toLowerCase();
    const bType = (getTypeName(workOrderTypes, b.workorder_type_id) || "").toLowerCase();
    const cmp = aType.localeCompare(bType);
    return sortOrder === "type_asc" ? cmp : -cmp;
  } else if (sortOrder === "status") {
    const aIsResolved = a.status?.trim().toLowerCase() === "resolved";
    const bIsResolved = b.status?.trim().toLowerCase() === "resolved";

    // push resolved orders to the end
    if (aIsResolved && !bIsResolved) return 1;
    if (!aIsResolved && bIsResolved) return -1;

    // for both active or both resolved → newest first
    return new Date(b.created_at) - new Date(a.created_at);
  } else {
    return 0;
  }
});
// normalize rows so DataGrid and Cards see the same keys
const normalizedRows = filteredWorkOrders.map(o => ({
  ...o,
  full_address: o.full_address || o.address || "",
  ClubMembershipTypeCode: o.ClubMembershipTypeCode || o.club_membership_type_code || "",
  created_at: o.created_at || o.createdAt || null,
  fullname: o.fullname || "",   // ensure not undefined
}));



  const paginatedWorkOrders = sortedWorkOrders.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  if (id) return <Outlet />;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: "1200px", margin: "0 auto" }}>
      {/* New Apple-style toolbar */}
      <FilterBar
        viewMode={viewMode}
        setViewMode={setViewMode}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filter={filter}
        setFilter={setFilter}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedNeighborhood={selectedNeighborhood}
        setSelectedNeighborhood={setSelectedNeighborhood}
        workOrderTypes={workOrderTypes}
        neighborhoodOptions={neighborhoodOptions}
        onReset={handleResetFilters}
        hideSpecialProperty={hideSpecialProperty}
        setHideSpecialProperty={setHideSpecialProperty}
      />

      <StatusFilterCards
        statuses={statuses}
        statusCounts={filteredStatusCounts}
        
        activeFilters={filterStatuses}
        onToggle={handleStatusFilter}
      />

      {viewMode === "cards" ? (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 2,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography variant="subtitle1" sx={{ alignSelf: "center" }}>
              Total: {filteredWorkOrders.length} work order
              {filteredWorkOrders.length !== 1 ? "s" : ""}
            </Typography>
          </Box>

          <Grid container spacing={5} alignItems="stretch">
            {paginatedWorkOrders.length === 0 ? (
              <Typography sx={{ mt: 2, ml: 1 }}>No work orders found.</Typography>
            ) : (
              paginatedWorkOrders.map((order) => (
                <Grid item key={order.order_id} xs={12} sm={6} md={4}>
                  <WorkOrderCard
                    order={order}
                    getAssignedUserName={(id) =>
                      getAssignedUserName(assignableUsers, id)
                    }
                    getTypeName={(id) => getTypeName(workOrderTypes, id)}
                    getStatusInfo={(status) => getStatusInfo(statuses, status)}
                  />
                </Grid>
              ))
            )}
          </Grid>

          {filteredWorkOrders.length > itemsPerPage && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
              <Pagination
                count={Math.ceil(filteredWorkOrders.length / itemsPerPage)}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      ) : (
        <Box
          sx={{
            width: "100%",
            overflowX: "auto",
            maxWidth: "100%",
            pb: 2,
            display: "block",
          }}
        >
          <Box sx={{ width: "max-content", minWidth: "1100px" }}>
            <WorkOrderTable
              rows={normalizedRows}
              getAssignedUserName={(id) => getAssignedUserName(assignableUsers, id)}
              getTypeName={(id) => getTypeName(workOrderTypes, id)}
              getStatusInfo={(status) => getStatusInfo(statuses, status)}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default WorkOrdersPage;
